// Speaker headshots are drawn as circles (`border-radius` on a square <img>).
// Email clients can't crop: crm-api's sanitizer strips `object-fit`, so a
// portrait photo in a square box is SQUASHED (2026-09-24: both speakers on the
// Gulf webinar were). So the editor crops every headshot to a square BEFORE
// uploading it — the email then only ever holds square images.

// The square to cut from a w×h image. Portraits keep the upper part (faces
// sit above centre): the slack is split 20% above / 80% below. Landscapes are
// centred.
export function squareCropRect(width: number, height: number): { x: number; y: number; size: number } {
  const size = Math.min(width, height)
  const x = Math.round((width - size) / 2)
  const y = height > width ? Math.round((height - size) * 0.2) : 0
  return { x, y, size }
}

// Largest side of the uploaded square — the email draws it at 72–112px, so
// 480 is sharp on retina without shipping megabytes.
export const HEADSHOT_MAX_PX = 480

// Crop an image file to a square JPEG. Resolves with the ORIGINAL file if the
// browser can't decode it (the upload still works; it just isn't squared).
export async function squareCropImageFile(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file)
    const { x, y, size } = squareCropRect(bitmap.width, bitmap.height)
    const out = Math.min(size, HEADSHOT_MAX_PX)
    const canvas = document.createElement("canvas")
    canvas.width = out
    canvas.height = out
    const ctx = canvas.getContext("2d")
    if (!ctx) return file
    ctx.drawImage(bitmap, x, y, size, size, 0, 0, out, out)
    bitmap.close?.()
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9))
    if (!blob) return file
    const name = file.name.replace(/\.[^.]+$/, "") + "-square.jpg"
    return new File([blob], name, { type: "image/jpeg" })
  } catch {
    return file
  }
}
